import React, { createContext, useContext, useState, useEffect } from 'react'

import { buildCartLineItem } from '../helpers/ordering'

const OrderingContext = createContext(null)

function storageKey(restaurantId) {
	return `foodi-cart-${restaurantId}`
}

function loadCart(restaurantId) {
	try {
		const raw = localStorage.getItem(storageKey(restaurantId))
		return raw ? JSON.parse(raw) : []
	} catch {
		return []
	}
}

function saveCart(restaurantId, items) {
	try {
		localStorage.setItem(storageKey(restaurantId), JSON.stringify(items))
	} catch {}
}

function calcTotal(items) {
	return items.reduce((sum, item) => sum + item.price * item.quantity, 0)
}

export const OrderingProvider = ({ children, restaurantId }) => {
	const [cartItems, setCartItems] = useState(() => loadCart(restaurantId))

	useEffect(() => {
		saveCart(restaurantId, cartItems)
	}, [restaurantId, cartItems])

	function addToCart(dish, { variation, modifiers } = {}) {
		setCartItems((prev) => {
			const lineItem = buildCartLineItem(dish, { variation, modifiers })
			const existing = prev.find((i) => i.lineItemId === lineItem.lineItemId)
			if (existing) {
				return prev.map((i) =>
					i.lineItemId === lineItem.lineItemId ? { ...i, quantity: i.quantity + 1 } : i,
				)
			}
			return [...prev, lineItem]
		})
	}

	function removeFromCart(lineItemId) {
		setCartItems((prev) => {
			const existing = prev.find((i) => i.lineItemId === lineItemId)
			if (!existing) return prev
			if (existing.quantity <= 1) return prev.filter((i) => i.lineItemId !== lineItemId)
			return prev.map((i) => (i.lineItemId === lineItemId ? { ...i, quantity: i.quantity - 1 } : i))
		})
	}

	function removeLineItem(lineItemId) {
		setCartItems((prev) => prev.filter((i) => i.lineItemId !== lineItemId))
	}

	function clearCart() {
		setCartItems([])
	}

	const total = calcTotal(cartItems)

	return (
		<OrderingContext.Provider
			value={{ cartItems, total, addToCart, removeFromCart, removeLineItem, clearCart }}
		>
			{children}
		</OrderingContext.Provider>
	)
}

export const useOrdering = () => useContext(OrderingContext)

export default OrderingContext
